import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import { CeAdminSupabaseAccountService } from "../../generic/server/supabase-account-service";

import type { UUID } from "../../../utils/uuid";
import type { FC } from "react";

export const AccountDhBrandCard: FC<{ accountId: UUID; accountName: string }> = ({
  accountId,
  accountName,
}) => {
  const { data, loading, error, reload } = useSupabaseLoader<number | null, "ce_admin">(
    async ({ supabase }) => {
      const { data: row, error: err } = await new CeAdminSupabaseAccountService(
        supabase,
      ).getCustomBrand(accountId);
      if (err) throw new Error("Could not load Design Huddle brand");
      return row?.content_custom_template_brand_brand_id ?? null;
    },
    [accountId],
    "ce_admin",
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  return (
    <DhBrandForm
      accountId={accountId}
      accountName={accountName}
      initialBrandId={data}
      onSaved={reload}
    />
  );
};

export const DhBrandForm: FC<{
  accountId: UUID;
  accountName: string;
  initialBrandId: number | null;
  onSaved: () => void;
}> = ({ accountId, accountName, initialBrandId, onSaved }) => {
  const [brandId, setBrandId] = useState(initialBrandId === null ? "" : String(initialBrandId));
  const [saving, setSaving] = useState(false);

  const persist = useSupabaseCallback(
    async ({ supabase }) => {
      const { error } = await new CeAdminSupabaseAccountService(supabase).setCustomBrand(
        accountId,
        brandId === "" ? undefined : Number(brandId),
      );

      if (error) {
        toast.error("Could not save Design Huddle brand");
        return;
      }

      toast.success(`Saved Design Huddle brand for account ${accountName}`);
      onSaved();
    },
    [accountId, accountName, brandId, onSaved],
    "ce_admin",
  );

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await persist();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={24} sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Design Huddle Custom Brand
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Custom Brand ID"
          type="number"
          variant="standard"
          value={brandId}
          onChange={(e) => setBrandId(e.target.value)}
          helperText="The Design Huddle brand ID used for custom content templates"
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            data-analytics-id="ce-admin-save-dh-brand"
            variant="contained"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
